// components/largebitcoinchart/BtcPriceChart.tsx
import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  type Chart,
  type ChartData,
  type ChartOptions,
  type Plugin,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

type Point = { dateISO: string; label: string; price: number };
type Highlight = { startIndex: number; endIndex: number };

const BtcPriceChart: React.FC<{
  data: Point[];                    // canonical series
  height?: number;
  highlight?: Highlight;
}> = ({ data, height = 320, highlight }) => {
  const labels = React.useMemo(() => data.map((d) => d.label), [data]);
  const prices = React.useMemo(() => data.map((d) => d.price), [data]);

  // IMPORTANT: ref typed as a LINE chart
  const chartRef = React.useRef<any>(null); // i dont know which the correct type

  // keep highlight current for plugin
  const hiRef = React.useRef<Highlight | undefined>(highlight);
  hiRef.current = highlight;

  // Strongly-typed plugin for a line chart
  const highlightPlugin = React.useMemo<Plugin<"line">>(
    () => ({
      id: "windowHighlight",
      beforeDatasetsDraw(chart: Chart<"line">) {
        const hi = hiRef.current;
        if (!hi) return;
        const { startIndex, endIndex } = hi;

        const meta = chart.getDatasetMeta(0);
        const pts = meta?.data;
        if (!pts || !pts[startIndex] || !pts[endIndex]) return;

        const x1 = (pts[startIndex] as any).x as number;
        const x2 = (pts[endIndex] as any).x as number;
        const yTop = chart.scales.y.top;
        const yBot = chart.scales.y.bottom;

        const { ctx } = chart;
        ctx.save();
        const grad = ctx.createLinearGradient(0, yTop, 0, yBot);
        grad.addColorStop(0, "rgba(249,115,22,0.16)");
        grad.addColorStop(1, "rgba(249,115,22,0.04)");
        ctx.fillStyle = grad;
        ctx.fillRect(Math.min(x1, x2), yTop, Math.abs(x2 - x1), yBot - yTop);

        ctx.strokeStyle = "rgba(249,115,22,0.6)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x1 + 0.5, yTop); ctx.lineTo(x1 + 0.5, yBot);
        ctx.moveTo(x2 + 0.5, yTop); ctx.lineTo(x2 + 0.5, yBot);
        ctx.stroke();
        ctx.restore();
      },
    }),
    []
  );

  // Force a cheap redraw when highlight changes
  React.useEffect(() => {
    chartRef.current?.update("none");
  }, [highlight?.startIndex, highlight?.endIndex]);

  // Type-safe data/options for a line chart
  const chartData = React.useMemo<ChartData<"line">>(
    () => ({
      labels,
      datasets: [
        {
          label: "Close Price",
          data: prices, // number[]
          borderColor: "#f97316",
          backgroundColor: (context) => {
            const ctx = context.chart.ctx;
            const gradient = ctx.createLinearGradient(0, 0, 0, context.chart.height);
            gradient.addColorStop(0, "rgba(249, 115, 22, 0.3)");
            gradient.addColorStop(1, "rgba(249, 115, 22, 0)");
            return gradient;
          },
          fill: true,
          tension: 0.3,
          pointRadius: 0,
        },
      ],
    }),
    [labels, prices]
  );

  const chartOptions = React.useMemo<ChartOptions<"line">>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = context.raw as number;
              return `Napi záró ár: ${value.toLocaleString(undefined, { maximumFractionDigits: 8 })}`;
            },
          },
        },
      },
      scales: {
        x: {
          ticks: { color: "#a3a3a3", maxRotation: 0, autoSkip: true },
          grid: { color: "rgba(255,255,255,0.08)" },
        },
        y: {
          ticks: { color: "#a3a3a3" },
          grid: { color: "rgba(255,255,255,0.08)" },
        },
      },
    }),
    []
  );

  return (
    <div className="w-full rounded-2xl bg-neutral-900 p-4 shadow-lg">
      <div className="mb-3 flex items-end justify-between">
        <h2 className="text-neutral-100 text-lg font-semibold">A <span className="text-orange-300">Bitcoin</span> árfolyama 2014-től.</h2>
      </div>
      <div style={{ height }}>
        <Line
          ref={chartRef}                        // now correctly typed
          data={chartData}
          options={chartOptions}
          plugins={[highlightPlugin]}
        />
      </div>
      <div className="mt-3 flex items-center gap-2 text-xs text-neutral-400">
        <span className="inline-block h-2 w-2 rounded-full" style={{ background: "#f97316" }} />
        <span>Napi záró ár</span>
      </div>
    </div>
  );
};

export default BtcPriceChart;
